<script>
    import { onMount } from "svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        Button,
        NotificationModal,
        PageLoading,
        SearchInput,
        SearchSelect,
        SvelteTable,
    } from "../../Components/Common/index.svelte";

    import { isMobile } from "../../stores";

    import {
        decodeSearchParams,
        encodeSearchParams,
        getFormatDate,
        getText,
        isObjectEmpty,
        replaceURL,
        showError,
        showException,
    } from "../../Utils/utilFunctions.svelte";

    import { deleteIcon, editIcon, eyeIcon } from "../../Utils/utilConstants.svelte";

    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";
    import { _getDisplayDate } from "../PatientEncounter/helpers/encounterHelper.svelte";
    import DateInput from "../../Components/Common/DateInput.svelte";
    import {
        addErrorInput,
        removeErrorInput,
    } from "../PatientEncounter/helpers/encounterUtils.svelte";
    import RichTextEditor from "../../Components/Custom/TextEditor/RichTextEditor.svelte";
    import CustomTooltip from "../../Components/Common/CustomTooltip.svelte";

    export let params;

    let isGettingOrderProducts = false,
        orderProducts = [];

    let createEditOrderProductModalOpen = false,
        isSavingOrderProduct = false,
        isConfirmEditOrderProductModalOpen = false;
    let isViewOrderProductModal = false;

    let deleteOrderProductModalOpen = false,
        selectedOrderProduct = { },
        isDeletingOrderProduct = false;

    let errInputs = [];
    const orderProductValidationPlaceholders = {
        orderProductNameInput: "Name",
        orderProductDateInput: "Date first offered",
        orderProductDistributionInput: "Distribution frequency",
        orderProductFeePercentInput: "Fee percent",
        orderProductFeePercentPipInput: "Fee percent PIP",
        orderProductFixedMonthlyFeeInput: "Fixed monthly fee",
        orderProductContentInput: "Order form content",
    };

    $: isEditOrderProduct = Boolean(selectedOrderProduct?.id);
    $: orderProductModalTitle = isViewOrderProductModal
        ? "View Order Product"
        : `${isEditOrderProduct ? "Edit" : "Create"} Order Product`;
    $: orderProductSubmitText = isViewOrderProductModal
        ? ""
        : isSavingOrderProduct
            ? "Saving"
            : isEditOrderProduct
                ? "Save Changes"
                : "Create";

    let filters = { };

    let page = 0,
        pageSize = 25,
        count;
    
    const distributionFrequencyOptions = [
        { value: 1, label: "Daily" },
        { value: 7, label: "Weekly" },
        { value: 14, label: "Fortnightly" },
        { value: 30, label: "Monthly" },
    ];

    const columns = [
        {
            key: "id",
            title: "Id",
            headerClass: "text-left px-3 ml-3 cursor-default rounded-tl-xl",
            class: "text-left px-3 cursor-pointer",
            value: (v) => getText(v.id),
        },
        {
            key: "name",
            title: "Name",
            headerClass: "text-left pl-3 w-1/5",
            class: "text-left pl-3 cursor-pointer",
            value: (v) => getText(v.name),
        },
        {
            key: "description",
            title: "Description",
            headerClass: "text-left pl-3 w-1/5",
            class: "px-3 break-all text-left",
            value: (v) => getText(v.description),
        },
        {
            key: "productDetails",
            title: "Configuration",
            headerClass: "text-left pl-3 w-full",
            class: "px-3 break-all text-left",
            value: (v) => getProductConfiguration(v),
        },
        {
            key: "default",
            title: "Default",
            headerClass: "text-left pl-3 w-1/5",
            class: "text-left pl-3 cursor-pointer",
            value: (v) => (v.default ? "Yes" : "No"),
        },
        {
            key: "buttons",
            title: "Actions",
            headerClass: "text-left pl-3 cursor-default rounded-tr-xl",
            class: "px-3 cursor-pointer",
            renderValue: (v) => _renderActionIcons(),
            csvExportHidden: true,
        },
    ];

    onMount(async () => {
        const orderProductIdFromURL = _getOrderProductIdFromURL(
            params?.query ?? "",
        );

        if (params.query) _updateSearchParamsByQuery(params.query);

        await loadOrderProducts(
            page * pageSize,
            pageSize,
            _getSearchParams(),
        );

        if (orderProductIdFromURL)
            await _openOrderProductById(orderProductIdFromURL);
    });

    const getProductConfiguration = (product) => {
        return `<b>Fee percent:</b> ${product.feePercent}%, <b>Fee percent PIP:</b> ${product.feePercentPIP}%, <b>Fixed monthly fee:</b> $${product.fixedMonthlyFee}, <b>Custody Funds:</b> ${product.custodyFunds ? "Yes" : "No"}, <b>Date first offered:</b> ${_getDisplayDate(product.dateFirstOffered)}, <b>Day frequency fund distribution:</b> ${product.dayFrequencyFundDistribution} day${product.dayFrequencyFundDistribution > 1 ? "s" :""}`;
    }

    const loadOrderProducts = async (start, limit, params) => {
        isGettingOrderProducts = true;

        await apiMethods.getMethod
            .getOrderProducts(start, limit, params)
            .then((res) => _handleOrderProducts(res))
            .catch((err) => showException(err));

        isGettingOrderProducts = false;
    };

    const checkOrderProductValidation = async () => {
        if (isViewOrderProductModal) return;

        if (isSavingOrderProduct) return;

        errInputs = [];

        _updateInvalidOrderProductFieldsStyle();

        if (errInputs.length) return;

        if (selectedOrderProduct && selectedOrderProduct.id) {
            isConfirmEditOrderProductModalOpen = true;
            return;
        }

        await _saveOrderProduct();
    };

    const confirmEditOrderProductUpdate = async () => {
        isConfirmEditOrderProductModalOpen = false;
        await _saveOrderProduct();
    };

    const cancelEditOrderProductUpdate = () => {
        isConfirmEditOrderProductModalOpen = false;
    };

    const _saveOrderProduct = async () => {
        isSavingOrderProduct = true;

        try {
            await createEditOrderProduct();
        } finally {
            isSavingOrderProduct = false;
        }
    };

    const createEditOrderProduct = async () => {
        if(!selectedOrderProduct.custodyFunds) { 
            //if no custody funds, set distribution to 0
            selectedOrderProduct.dayFrequencyFundDistribution = 0;
        }
        if(selectedOrderProduct.id) {
            //edit
            await apiMethods.putMethod
                .editOrderProduct(selectedOrderProduct)
                .then((res) => {
                    if (!res || res.error)
                        showError(
                            "Failed to edit order product, please refresh the page and try again and contact support if the issue persists.",
                        );
                    else
                        loadOrderProducts(
                            page * pageSize,
                            pageSize,
                            _getSearchParams(),
                        ); 
                })
                .catch((err) => showException(err));
        } else {
            //create
            await apiMethods.postMethod
                .createOrderProduct(selectedOrderProduct)
                .then((res) => {
                    if (!res || res.error)
                        showError(
                            "Failed to create order product, please refresh the page and try again and contact support if the issue persists.",
                        );
                    else
                        loadOrderProducts(
                            page * pageSize,
                            pageSize,
                            _getSearchParams(),
                        );
                })
                .catch((err) => showException(err));
        }

        _closeCreateEditOrderProductModal();
    };

    const _updateInvalidOrderProductFieldsStyle = () => {
        if (!_hasTextValue(selectedOrderProduct.name))
            errInputs = addErrorInput(
                "orderProductNameInput",
                errInputs,
                orderProductValidationPlaceholders.orderProductNameInput,
            );
        else
            errInputs = removeErrorInput(
                "orderProductNameInput",
                errInputs,
                orderProductValidationPlaceholders.orderProductNameInput,
            );

        if (!_hasValue(selectedOrderProduct.dateFirstOffered))
            errInputs = addErrorInput(
                "orderProductDateInput",
                errInputs,
                orderProductValidationPlaceholders.orderProductDateInput,
            );
        else
            errInputs = removeErrorInput(
                "orderProductDateInput",
                errInputs,
                orderProductValidationPlaceholders.orderProductDateInput,
            );

        if (
            !_isPositiveInteger(
                selectedOrderProduct.dayFrequencyFundDistribution,
            )
        )
            errInputs = addErrorInput(
                "orderProductDistributionInput",
                errInputs,
                orderProductValidationPlaceholders.orderProductDistributionInput,
            );
        else
            errInputs = removeErrorInput(
                "orderProductDistributionInput",
                errInputs,
                orderProductValidationPlaceholders.orderProductDistributionInput,
            );

        if (!_isNonNegativeNumber(selectedOrderProduct.feePercent))
            errInputs = addErrorInput(
                "orderProductFeePercentInput",
                errInputs,
                orderProductValidationPlaceholders.orderProductFeePercentInput,
            );
        else
            errInputs = removeErrorInput(
                "orderProductFeePercentInput",
                errInputs,
                orderProductValidationPlaceholders.orderProductFeePercentInput,
            );

        if (!_isNonNegativeNumber(selectedOrderProduct.feePercentPIP))
            errInputs = addErrorInput(
                "orderProductFeePercentPipInput",
                errInputs,
                orderProductValidationPlaceholders.orderProductFeePercentPipInput,
            );
        else
            errInputs = removeErrorInput(
                "orderProductFeePercentPipInput",
                errInputs,
                orderProductValidationPlaceholders.orderProductFeePercentPipInput,
            );

        if (!_isNonNegativeNumber(selectedOrderProduct.fixedMonthlyFee))
            errInputs = addErrorInput(
                "orderProductFixedMonthlyFeeInput",
                errInputs,
                orderProductValidationPlaceholders.orderProductFixedMonthlyFeeInput,
            );
        else
            errInputs = removeErrorInput(
                "orderProductFixedMonthlyFeeInput",
                errInputs,
                orderProductValidationPlaceholders.orderProductFixedMonthlyFeeInput,
            );

        if (!_hasTextValue(selectedOrderProduct.content))
            errInputs = addErrorInput(
                "orderProductContentInput",
                errInputs,
                orderProductValidationPlaceholders.orderProductContentInput,
            );
        else
            errInputs = removeErrorInput(
                "orderProductContentInput",
                errInputs,
                orderProductValidationPlaceholders.orderProductContentInput,
            );
    };

    const _hasValue = (value) =>
        value !== undefined && value !== null && value !== "";

    const _hasTextValue = (value) => {
        if (typeof value === "string") {
            const plainText = value
                .replace(/<[^>]*>/g, "")
                .replace(/&nbsp;/g, " ")
                .trim();

            return plainText.length > 0;
        }

        return value !== undefined && value !== null;
    };

    const _toNumber = (value) => {
        if (value === "" || value === null || value === undefined) return NaN;

        if (typeof value === "string") {
            const trimmedValue = value.trim();

            if (!trimmedValue) return NaN;

            return Number(trimmedValue);
        }

        if (typeof value === "number") return value;

        const parsedValue = Number(value);

        return Number.isFinite(parsedValue) ? parsedValue : NaN;
    };

    const _isNonNegativeNumber = (value) => {
        const parsedValue = _toNumber(value);

        return Number.isFinite(parsedValue) && parsedValue >= 0;
    };

    const _isPositiveInteger = (value) => {
        const parsedValue = _toNumber(value); 

        return Number.isInteger(parsedValue) && parsedValue > 0;
    };

    const _resetOrderProductValidationState = () => {
        const currentErrInputs = [...errInputs];

        currentErrInputs.forEach((id) => {
            errInputs = removeErrorInput(
                id,
                errInputs,
                orderProductValidationPlaceholders[id] ?? "",
            );
        });

        errInputs = [];
    };

    const deleteOrderProduct = async () => {
        isDeletingOrderProduct = true;

        await apiMethods.deleteMethod
            .deleteOrderProduct(selectedOrderProduct.id)
            .then((res) => {
                if (!res)
                    showError(
                        "Failed to delete order product, please refresh the page and try again and contact support if the issue persists.",
                    );
                else if(res.error) {
                    showError(
                        res.error
                    );
                }
                else
                    loadOrderProducts(
                        page * pageSize,
                        pageSize,
                        _getSearchParams(),
                    );
            })
            .catch((err) => showException(err));

        _closeDeleteOrderProductModal();

        isDeletingOrderProduct = false;
    };

    const handleFilterSelect = () => {
        page = 0;

        updateURL();

        loadOrderProducts(0, pageSize, _getSearchParams());
    };

    const updateURL = () => {
        replaceURL(
            `/OrderProducts/page=${page}&size=${pageSize}${_getParamsString()}`,
        );
    };

    const handleClickCell = (event, rowData, key) => {
        const { className } = event.target;

        if (key == "buttons") {
            if (className.includes("deleteIcon"))
                _openDeleteOrderProductModal(rowData);
            else if (className.includes("editIcon"))
                _openCreateEditOrderProductModal(rowData);
            else if (className.includes("viewIcon"))
                _openCreateEditOrderProductModal(rowData, { viewMode: true });
        }
    };

    const handleClickCol = (event, colData, key) => {};

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadOrderProducts(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadOrderProducts(0, pageSize, _getSearchParams());
    };

    // HELPER FUNCTIONS
    const _handleOrderProducts = (res) => {
        if (!res)
            showError(
                "Failed to load order products, please refresh the page and try again and contact support if the issue persists.",
            );
        else if (res.error) showError(res.error);
        else {
            orderProducts = [...res.results];
            count = res.total;
        }
    };

    const _updateSearchParamsByQuery = (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    pageSize = Number(arr[i].split("=")[1]);

                if (arr[i].includes("search="))
                    filters.search = arr[i].split("=")[1].toString();
               
            }

            updateURL();
        }
    };

    const _getSearchParams = () => {
        let params = {};

        if (filters.search) params.search = filters.search;

        return params;
    };

    const _getParamsString = () => {
        let str = "";

        const params = _getSearchParams();

        if (params) {
            if (params.search) str += `&search=${params.search}`;
        }

        return encodeSearchParams(str);
    };

    const _renderActionIcons = () => {
        return `<div class="flex flex-1 items-center justify-start gap-x-2">
                    <div class="viewIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="View Order Product">
                        <i class="viewIcon ${eyeIcon}"></i>
                    </div>
                    <div class="editIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Edit Order Product">
                        <i class="editIcon ${editIcon}"></i>
                    </div>

                    <div class="deleteIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Delete Order Product">
                        <i class="deleteIcon ${deleteIcon}"></i>
                    </div>
                </div>`;
    };

    const _openCreateEditOrderProductModal = (product, { viewMode = false } = {}) => {
        createEditOrderProductModalOpen = true;
        isViewOrderProductModal = viewMode;
        selectedOrderProduct = { ...(product ?? {}) };
    };

    const _closeCreateEditOrderProductModal = () => {
        createEditOrderProductModalOpen = false;
        isConfirmEditOrderProductModalOpen = false;
        isViewOrderProductModal = false;
        _resetOrderProductValidationState();
        selectedOrderProduct = {};
    };

    const _openDeleteOrderProductModal = (product) => {
        deleteOrderProductModalOpen = true;

        selectedOrderProduct = product;
    };

    const _closeDeleteOrderProductModal = () => {
        deleteOrderProductModalOpen = false;

        selectedOrderProduct = null;
    };

    const _getOrderProductIdFromURL = (query) => {
        if (query) {
            const arr = decodeSearchParams(query).split("&");

            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("id=")) {
                    const idValue = Number(arr[i].split("=")[1]);

                    if (!Number.isNaN(idValue)) return idValue;
                }
            }
        }

        if (typeof window === "undefined") return null;

        const searchParams = new URLSearchParams(window.location.search ?? "");
        const idParam = searchParams.get("id");

        if (!idParam) return null;

        const parsedId = Number(idParam);

        return Number.isNaN(parsedId) ? null : parsedId;
    };

    const _openOrderProductById = async (id) => {
        if (!id) return;

        const productInList = orderProducts.find((product) => product.id === id);

        if (productInList) {
            _openCreateEditOrderProductModal(productInList, { viewMode: true });
            _removeOrderProductIdQueryParam();
            return;
        }

        try {
            const response = await apiMethods.getMethod.getOrderProducts(
                0,
                1,
                { id },
            );

            const fetchedProduct = response?.results?.[0];

            if (fetchedProduct) {
                _openCreateEditOrderProductModal(fetchedProduct, {
                    viewMode: true,
                });
                _removeOrderProductIdQueryParam();
            } else showError("Order product not found.");
        } catch (error) {
            showException(error);
        }
    };

    const _removeOrderProductIdQueryParam = () => {
        if (typeof window === "undefined") return;

        const url = new URL(window.location.href);

        if (!url.searchParams.has("id")) return;

        url.searchParams.delete("id");

        const newPath = `${url.pathname}${url.search}${url.hash}`;

        replaceURL(newPath);
    };

    let editorRef;
</script>

<div class="flex flex-col flex-1 gap-y-4">
    <div
        class="flex flex-0 {$isMobile
            ? 'flex-col gap-y-4 items-start'
            : 'justify-between items-end'}"
    >
        <div class="flex gap-4 flex-wrap">
            <SearchInput
                bind:value={filters.search}
                title="Search"
                inputFunc={handleFilterSelect}
                isLong
            />
           
        </div>

        <div class="flex justify-end {$isMobile ? '' : 'max-w-[50%]'}">
            <Button clickFunc={() => _openCreateEditOrderProductModal({}) }>
                Create Order Product
            </Button>
        </div>
    </div>

    <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
        {#if isGettingOrderProducts}
            <PageLoading />
        {:else}
            <div
                class={$isMobile
                    ? "overflow-x-auto overflow-y-visible"
                    : "overflow-y-auto absolute inset-0"}
            >
                <SvelteTable
                    {columns}
                    rows={orderProducts}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                />
            </div>
        {/if}
    </div>

    <TableFooter
        {page}
        {pageSize}
        {count}
        tableData={orderProducts}
        {onPageChange}
        {onPageSizeChange}
        csvConfig={{
            columns,
            data: orderProducts,
            fileName: "OrderProducts",
            allDataApi: async () =>
                await apiMethods.getMethod.getOrderProducts(
                    0,
                    999999,
                    _getSearchParams(),
                ),
        }}
    />
</div>

<NotificationModal
    title={orderProductModalTitle}
    submitText={orderProductSubmitText}
    isModalOpen={createEditOrderProductModalOpen}
    isConfirming={isSavingOrderProduct}
    closeModal={_closeCreateEditOrderProductModal}
    confirmAction={checkOrderProductValidation}
    closeModalFunction={_closeCreateEditOrderProductModal}
    showConfirmBtn={!isViewOrderProductModal}
    noContentPadding={true}
    modalMaxWidth="max-w-6xl"
>
    <div class="flex flex-col gap-y-4 p-8">
        <div class="flex flex-row gap-x-4"> 
            <div class="flex-1 flex gap-x-4 items-center">
                <p class="title">Name:</p>
                <input
                    type="text"
                    id="orderProductNameInput"
                    bind:value={selectedOrderProduct.name}
                    class="input input-bordered input-sm w-remaining"
                    autocomplete="off"
                    placeholder="Name"
                    disabled={isViewOrderProductModal}
                />
            </div>
            <div class="flex gap-x-4 items-center">
                <p class="title">
                     <CustomTooltip tooltipPosition="top">
                        <div class="cursor-pointer" slot="tooltipTrigger">
                            <i
                                class="fa-solid fa-circle-info"
                            >
                            </i>
                        </div>
                    
                        <p>Default order product will be set automatically on new doctors after the onboarding process.</p>
                    </CustomTooltip>
                    Default:
                </p>
                <div class="w-remaining">
                    <input
                        type="checkbox"
                        class="toggle toggle-secondary"
                        bind:checked={selectedOrderProduct.default}
                        disabled={isViewOrderProductModal}
                    />
                </div>
            </div>
        </div>
       
        <div class="flex gap-x-4 items-center">
            <p class="title">Description:</p>
            <textarea
                id="orderProductDescriptionInput"
                class="textarea textarea-bordered w-remaining"
                rows="2"
                autocomplete="off"
                bind:value={selectedOrderProduct.description}
                placeholder="Description"
                disabled={isViewOrderProductModal}
            />
        </div>
        <div class="flex gap-x-4 items-center">
            <div class="flex-1 flex gap-x-4 items-center">
                <p class="title">Date first offered:</p>
                <div class="w-remaining pl-1">
                    <div
                        id="date-input-wrapper"
                        on:pointerdown={() => {}}
                    >
                        <DateInput
                            showTime={false}
                            minDate="1900-01-01"
                            wrapperClass="input input-sm input-bordered"
                            wrapperClassFocus="inputFocus"
                            id="orderProductDateInput"
                            placeholder="Date first offered"
                            bind:value={selectedOrderProduct.dateFirstOffered}
                            isDisable={isViewOrderProductModal}
                        />
                    </div>
                </div>
            </div>
            <div class="flex-1 flex gap-x-4 items-center">
                    <p class="title">Fixed monthly fee ($):</p>
                    <div class="w-remaining">
                        <input
                            type="number"
                            id="orderProductFixedMonthlyFeeInput"
                            bind:value={selectedOrderProduct.fixedMonthlyFee}
                            class="input input-bordered input-sm w-full"
                            autocomplete="off"
                            min="0"
                            placeholder="Fixed monthly fee"
                            disabled={isViewOrderProductModal}
                        />
                    </div>
            </div>
        </div>
        <div class="flex gap-x-4 items-center">
            <div class="flex-1 flex gap-x-4 items-center">
                <p class="title">Fee %:</p>
                <div class="w-remaining pl-1">
                    <input
                        type="number"
                        id="orderProductFeePercentInput"
                        bind:value={selectedOrderProduct.feePercent}
                        class="input input-bordered input-sm w-full"
                        autocomplete="off"
                        min="0"
                        placeholder="Fee percent"
                        disabled={isViewOrderProductModal}
                    />
                </div>  
            </div>
            <div class="flex-1 flex gap-x-4 items-center">
                <p class="title">Fee % PIP:</p>
                <input
                    type="number"
                    id="orderProductFeePercentPipInput"
                    bind:value={selectedOrderProduct.feePercentPIP}
                    class="input input-bordered input-sm w-remaining"
                    autocomplete="off"
                    min="0"
                    placeholder="Fee percent PIP"
                    disabled={isViewOrderProductModal}
                />
            </div>
        </div>
        <div class="flex gap-x-4 items-center">
            <div class="flex-1 flex gap-x-4 items-center">
                <p class="title">Custody Funds:</p>
                <div class="w-remaining">
                    <input
                        type="checkbox"
                        class="toggle toggle-secondary"
                        bind:checked={selectedOrderProduct.custodyFunds}
                        disabled={isViewOrderProductModal}
                    />
                </div>
            </div>
            <div class="flex-1 flex gap-x-4 items-center">
                {#if selectedOrderProduct.custodyFunds}
                    <p class="title">Distribution frequency:</p>
                    <div class="w-remaining">
                        <select
                            id="agreementDocumentTypeInput"
                            bind:value={selectedOrderProduct.dayFrequencyFundDistribution}
                            class="select select-bordered select-sm w-full"
                            disabled={isViewOrderProductModal}
                        >
                            <option value=0>None</option>
                            {#each distributionFrequencyOptions as option}
                                <option value={option.value}>{option.label}</option>
                            {/each}
                        </select>
                    </div>
                {/if}
            </div>
        </div>
        <div class="flex gap-x-4 items-start h-[500px]">
            <p class="title mt-2">Order Form content user agrees to:</p>
            <div
                id="orderProductContentInput"
                class="flex flex-1 w-remaining h-full content-editor"
            >
                <RichTextEditor
                    bind:content={selectedOrderProduct.content}
                    bind:this={editorRef}
                    config={{ isDisabled: isViewOrderProductModal }}
                />
            </div>
        </div>
    </div>
</NotificationModal>

<NotificationModal
    title="Confirm Update"
    isModalOpen={isConfirmEditOrderProductModalOpen}
    closeModal={cancelEditOrderProductUpdate}
    closeModalFunction={cancelEditOrderProductUpdate}
    confirmAction={confirmEditOrderProductUpdate}
    submitText="Yes"
    cancelText="No"
    isReverseBtns
>
    Modifying an existing order product automatically initiates the agreement collection process for all doctors linked to that product.
</NotificationModal>

<NotificationModal
    title="Delete Order Product"
    submitText={isDeletingOrderProduct ? "Deleting" : "Delete"}
    confirmButtonClass="btn-error"
    cancelButtonClass="btn-accent"
    isModalOpen={deleteOrderProductModalOpen}
    isConfirming={isDeletingOrderProduct}
    closeModal={_closeDeleteOrderProductModal}
    closeModalFunction={_closeDeleteOrderProductModal}
    confirmAction={deleteOrderProduct}
>
    Are you sure you want to delete the order product: {selectedOrderProduct.name}?
</NotificationModal>

<style>
    .title {
        width: 170px;
    }

    .w-remaining {
        width: calc(100% - 170px);
    }

    .content-editor {
        height: 100%;
        min-height: 100%;
        overflow-y: auto;
    }

    .content-editor :global(.ql-editor) {
        width: 100%;
        height: 100%;
        overflow-y: auto;
    }

    .content-editor :global(.ql-container.ql-disabled),
    .content-editor :global(.ql-container.ql-disabled .ql-editor) {
        pointer-events: auto;
        cursor: default;
    }
</style>
